/**
 * Context + types for `ChangeRequestConfirmProvider`. Split from the
 * provider component so the file can be imported by hooks without
 * tripping `react-refresh/only-export-components`.
 */
import { createContext, useContext } from 'react';

export interface ChangeRequestConfirmation {
  requesterComment: string;
}

export interface ChangeRequestConfirmContextValue {
  /**
   * Open the confirm modal. Resolves with `{ requesterComment }` if the
   * user confirms, or `null` if they cancel / dismiss. Only one prompt can
   * be active at a time — a second call while one is open settles the
   * previous prompt as cancelled (defensive against double-fire from
   * stacked mutation retries).
   */
  requestConfirmation: () => Promise<ChangeRequestConfirmation | null>;
  /**
   * Record that the just-completed gated mutation RAISED A CHANGE REQUEST
   * (rather than applying directly). Called by `withChangeRequestConfirm`
   * after a confirmed replay succeeds. FormContext reads this via
   * `consumeChangeRequestCreated` to show a "submitted for approval" toast
   * (not a false "updated successfully") and to keep the user on the entity
   * so the pending state is visible (RSP-6099 findings I/K).
   */
  markChangeRequestCreated: () => void;
  /**
   * Read-and-reset the flag set by `markChangeRequestCreated`. Returns true
   * exactly once per CR creation. FormContext clears any stale value at the
   * start of each submit (a gated delete sets the flag but never routes
   * through FormContext) and reads the fresh value after the mutation.
   */
  consumeChangeRequestCreated: () => boolean;
}

export const ChangeRequestConfirmContext =
  createContext<ChangeRequestConfirmContextValue | null>(null);

export const useChangeRequestConfirm = (): ChangeRequestConfirmContextValue => {
  const ctx = useContext(ChangeRequestConfirmContext);
  if (!ctx) {
    throw new Error(
      'useChangeRequestConfirm must be used within ChangeRequestConfirmProvider'
    );
  }

  return ctx;
};
